import React, { useState } from 'react';
import { Flex } from '@atlaskit/primitives';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { useCountCompassComponents } from '../../common/services/count-compass-components/index.tsx';
import { LoadingState } from '../loading-state/loading-state.tsx';
import CompassCrossFlow from './cross-flow/view.tsx';
import { ComponentsTableEmptyState } from './empty-state/components-table-empty-state.tsx';

const TEST_ID = 'project-directories.components.compass-empty-state.empty';

type Props = {
	projectKey: string;
};

export const CompassEmptyState = ({ projectKey }: Props) => {
	const licensedProducts = useLicensedProducts();
	const [crossFlowComplete, setCrossFlowComplete] = useState<boolean>(false);

	let componentsTableEmptyState;

	const { count, loading, error } = useCountCompassComponents({
		skip: !((licensedProducts && licensedProducts.compass) || crossFlowComplete),
	});

	// Render empty state if tenant has Compass, OR
	// We've completed cross flow. Licensed products won't be updated until page reload.
	if ((licensedProducts && licensedProducts.compass) || crossFlowComplete) {
		if (loading) {
			componentsTableEmptyState = <LoadingState />;
		} else if (error) {
			componentsTableEmptyState = (
				<ComponentsTableEmptyState
					testId={TEST_ID}
					projectKey={projectKey}
					loading={false}
					displayMode="unknown"
				/>
			);
		} else if (!count) {
			componentsTableEmptyState = (
				<ComponentsTableEmptyState
					testId={TEST_ID}
					projectKey={projectKey}
					loading={false}
					displayMode="no-components"
				/>
			);
		} else {
			componentsTableEmptyState = (
				<ComponentsTableEmptyState
					testId={TEST_ID}
					projectKey={projectKey}
					loading={false}
					componentCount={count}
					displayMode="no-linked-components"
				/>
			);
		}
	} else {
		componentsTableEmptyState = (
			<Flex alignItems="center" justifyContent="center">
				<CompassCrossFlow
					origin="project-pages.project-directories.components.view.empty"
					onCrossFlowComplete={() => setCrossFlowComplete(true)}
				/>
			</Flex>
		);
	}

	return (
		<JSErrorBoundary
			fallback="unmount"
			packageName="jiraProjectDirectoriesComponents"
			teamName="compass-lodestone"
			id="compassEmptyState"
			sendToPrivacyUnsafeSplunk
		>
			{componentsTableEmptyState}
		</JSErrorBoundary>
	);
};
